class TestimonialsSplit {
  static boot() {
    if (typeof Swiper === 'undefined') {
      window.setTimeout(() => TestimonialsSplit.boot(), 50);
      return;
    }
    TestimonialsSplit.initAll();
  }

  static initAll() {
    document.querySelectorAll('[data-testimonials-split]').forEach((root) => {
      if (root.dataset.testimonialsSplitInit === 'true') return;
      TestimonialsSplit._whenNear(root, () => {
        if (root.dataset.testimonialsSplitInit === 'true') return;
        new TestimonialsSplit(root);
      });
    });
  }

  // Lazy-init: build the slider only when the section nears the viewport, so
  // below-the-fold Swipers don't run on initial page load (cuts JS exec / TBT).
  // In the theme editor (designMode) init immediately for live preview.
  static _whenNear(el, cb) {
    if (window.Shopify && window.Shopify.designMode) { cb(); return; }
    if (el.dataset.lazyInitObserved === 'true') return;
    el.dataset.lazyInitObserved = 'true';
    if (!('IntersectionObserver' in window)) { cb(); return; }
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { obs.unobserve(entry.target); cb(); }
      });
    }, { rootMargin: '300px 0px' });
    io.observe(el);
  }

  constructor(root) {
    this.root = root;
    this.contentEl = root.querySelector('.js-testimonials-split-content');
    this.mediaEl = root.querySelector('.js-testimonials-split-media');
    this.progressFill = root.querySelector('.js-testimonials-split-progress-fill');
    this.counterEl = root.querySelector('.js-testimonials-split-counter');
    this.prevBtn = root.querySelector('.js-testimonials-split-prev');
    this.nextBtn = root.querySelector('.js-testimonials-split-next');

    if (!this.contentEl || !this.mediaEl || !this.progressFill || !this.counterEl) return;

    root.dataset.testimonialsSplitInit = 'true';

    this.slideCount = parseInt(root.dataset.slideCount, 10) || this.contentEl.querySelectorAll('.swiper-slide').length;
    this.autoplayEnabled = root.dataset.autoplay === 'true';
    this.loop = root.dataset.loop === 'true';
    this.speed = parseInt(root.dataset.speed, 10) || 500;
    this.transitionSpeed = Math.min(this.speed + 200, 1200);
    this.autoplayDelay = parseInt(root.dataset.autoplayDelay, 10) || 5000;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.destroySwipers();
    this.initSwipers();
    this.syncUi();
  }

  destroySwipers() {
    if (this.contentSwiper) {
      try {
        this.contentSwiper.destroy(true, true);
      } catch (error) {}
      this.contentSwiper = null;
    }

    if (this.mediaSwiper) {
      try {
        this.mediaSwiper.destroy(true, true);
      } catch (error) {}
      this.mediaSwiper = null;
    }
  }

  initSwipers() {
    const autoplay =
      this.autoplayEnabled && !this.reducedMotion
        ? {
            delay: this.autoplayDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false;

    this.mediaSwiper = new Swiper(this.mediaEl, {
      slidesPerView: 1,
      speed: this.transitionSpeed,
      loop: this.loop,
      effect: 'fade',
      fadeEffect: { crossFade: true },
      allowTouchMove: true,
      autoplay: false,
    });

    this.contentSwiper = new Swiper(this.contentEl, {
      slidesPerView: 1,
      speed: this.transitionSpeed,
      loop: this.loop,
      allowTouchMove: true,
      autoplay,
      watchSlidesProgress: true,
      navigation: {
        nextEl: this.nextBtn,
        prevEl: this.prevBtn,
      },
      controller: {
        control: this.mediaSwiper,
      },
    });

    this.mediaSwiper.controller.control = this.contentSwiper;

    this.bindSlideOpacity(this.contentSwiper);

    const onSlideChange = () => this.syncUi();
    this.contentSwiper.on('slideChangeTransitionStart', onSlideChange);
    this.mediaSwiper.on('slideChangeTransitionStart', onSlideChange);
  }

  syncUi() {
    if (!this.contentSwiper) return;

    const currentIndex = this.contentSwiper.realIndex + 1;
    this.updateProgressByIndex(currentIndex);
    this.updateCounter(currentIndex);
    this.bindNavState();
  }

  bindSlideOpacity(swiper) {
    if (this.reducedMotion) return;

    const updateOpacity = () => {
      swiper.slides.forEach((slide) => {
        const distance = Math.min(Math.abs(slide.progress || 0), 1);
        slide.style.opacity = String(1 - distance * 0.65);
      });
    };

    const syncTransition = (_swiper, duration) => {
      swiper.slides.forEach((slide) => {
        slide.style.transitionProperty = 'opacity, transform';
        slide.style.transitionDuration = `${duration}ms`;
        slide.style.transitionTimingFunction = 'ease';
      });
    };

    swiper.on('setTranslate', updateOpacity);
    swiper.on('setTransition', syncTransition);
    swiper.on('slideChangeTransitionStart', updateOpacity);
    swiper.on('transitionEnd', updateOpacity);
    swiper.on('init', updateOpacity);
    updateOpacity();
  }

  bindNavState() {
    if (!this.prevBtn || !this.nextBtn || this.loop) {
      if (this.prevBtn) this.prevBtn.disabled = false;
      if (this.nextBtn) this.nextBtn.disabled = false;
      return;
    }

    this.prevBtn.disabled = this.contentSwiper.isBeginning;
    this.nextBtn.disabled = this.contentSwiper.isEnd;
  }

  updateProgressByIndex(currentIndex) {
    if (this.slideCount <= 0) return;

    const scale = TestimonialsSplit.clamp(currentIndex / this.slideCount, 0, 1);
    this.progressFill.style.transform = `scaleX(${scale})`;
  }

  updateCounter(currentIndex) {
    const safeIndex = TestimonialsSplit.clamp(currentIndex, 1, this.slideCount);
    this.counterEl.textContent = `${safeIndex}/${this.slideCount}`;
  }

  static clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => TestimonialsSplit.boot());
} else {
  TestimonialsSplit.boot();
}

document.addEventListener('shopify:section:load', (event) => {
  const root = event.target.querySelector('[data-testimonials-split]');
  if (!root) return;
  root.dataset.testimonialsSplitInit = '';
  if (typeof Swiper === 'undefined') {
    TestimonialsSplit.boot();
    return;
  }
  new TestimonialsSplit(root);
});
